import type {
  AnyDatapoint,
  DatapointMetadata,
  XYChartAxisInternal,
  XYChartStackingValueBoundaries,
} from '../../types/xy-chart-internals.js';
import type { AreaDatapoint, BarDatapoint } from '../../types/xy-chart.js';
import {
  getStackedValue,
  getStackingData,
} from '../utils/stacking/stack-datapoints.js';

export interface StackingResult {
  calcDatapoint: AnyDatapoint;
  datapointMetadata: DatapointMetadata;
  shouldContinue: boolean;
}

/**
 * A class that encapsulates stacking operations for series variants.
 * This class can be instantiated by any series strategy that needs stacking functionality.
 */
interface StackDomainData {
  totalPositive: number;
  totalNegative: number;
}

export class StackableSeriesHelper {
  private static readonly STARTING_POINT = 0;
  private readonly stackGroups: Map<string, XYChartStackingValueBoundaries>;
  private readonly stackDomainData: Map<string, StackDomainData>;

  constructor() {
    this.stackGroups = new Map();
    this.stackDomainData = new Map();
  }

  /**
   * Accumulates total positive and negative values per stack key.
   * Should be called during the preprocess phase before processing individual datapoints.
   * This is needed so that binDomain can be included in metadata for relative stacking.
   */
  processStackDomain(
    datapoint: BarDatapoint | AreaDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ): void {
    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
    );

    if (typeof value === 'number') {
      const acc = this.stackDomainData.get(stackKey) ?? {
        totalPositive: StackableSeriesHelper.STARTING_POINT,
        totalNegative: StackableSeriesHelper.STARTING_POINT,
      };
      if (value >= 0) {
        acc.totalPositive += value;
      } else {
        acc.totalNegative += Math.abs(value);
      }
      this.stackDomainData.set(stackKey, acc);
    }
  }

  /**
   * Returns the total bin domain for a given stack key.
   * The bin domain is the total height of the stack, including both positive and negative values.
   * Note: totalNegative is already stored as an absolute value.
   */
  getStackBinDomain(stackKey: string): number | undefined {
    const domainData = this.stackDomainData.get(stackKey);
    if (!domainData) {
      return undefined;
    }

    return domainData.totalPositive + domainData.totalNegative;
  }

  /**
   * Processes a datapoint for stacking, calculating the offset and updating stack groups.
   * Returns the calculated datapoint with stacking offsets applied and enriched metadata.
   */
  processStackedDatapoint(
    datapoint: BarDatapoint | AreaDatapoint,
    chartOrientation: 'vertical' | 'horizontal',
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    datapointMetadata: DatapointMetadata,
  ): StackingResult {
    const { stackKey, value } = getStackingData(
      chartOrientation,
      datapoint,
      xAxis,
      yAxis,
    );

    if (typeof value !== 'number') {
      return {
        calcDatapoint: {
          x0: datapoint.x0,
          x1: datapoint.x1,
          y0: datapoint.y0,
          y1: datapoint.y1,
        },
        datapointMetadata,
        shouldContinue: true,
      };
    }

    const prevValue = this.stackGroups.get(stackKey) ?? {
      minBoundary: StackableSeriesHelper.STARTING_POINT,
      maxBoundary: StackableSeriesHelper.STARTING_POINT,
    };

    const binDomain = this.getStackBinDomain(stackKey);

    const updatedMetadata: DatapointMetadata = {
      ...datapointMetadata,
      accumulator: value > 0 ? prevValue.maxBoundary : prevValue.minBoundary,
      binDomain,
    };

    const stackingOffset =
      value > 0 ? prevValue.maxBoundary : prevValue.minBoundary;

    const calcDatapoint: BarDatapoint = {
      x0:
        chartOrientation === 'horizontal' && typeof datapoint.x0 === 'number'
          ? datapoint.x0 + stackingOffset
          : datapoint.x0,
      x1:
        chartOrientation === 'horizontal' && typeof datapoint.x1 === 'number'
          ? datapoint.x1 + stackingOffset
          : datapoint.x1,
      y0:
        chartOrientation === 'vertical' && typeof datapoint.y0 === 'number'
          ? datapoint.y0 + stackingOffset
          : datapoint.y0,
      y1:
        chartOrientation === 'vertical' && typeof datapoint.y1 === 'number'
          ? datapoint.y1 + stackingOffset
          : datapoint.y1,
    };

    this.stackGroups.set(stackKey, {
      ...prevValue,
      ...getStackedValue(value, prevValue),
    });

    return {
      calcDatapoint,
      datapointMetadata: updatedMetadata,
      shouldContinue: false,
    };
  }
}
